import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, RefreshCw, UploadCloud, Trash2, Plus, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

function StatusBadge({ ok, label }) {
  return (
    <span className={`text-[12px] uppercase tracking-wide font-semibold px-2 py-1 rounded-md ${ok ? "oc-status-fair" : "oc-status-excessive"}`}>
      {label}: {ok ? "OK" : "Niet ingesteld"}
    </span>
  );
}

function LogoUpload({ variant, currentUrl, label, dark, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("variant", variant);
      form.append("file", file);
      const { data } = await api.post("/admin/branding/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${label} bijgewerkt`);
      onChange?.(data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload mislukt");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm("Logo verwijderen?")) return;
    await api.delete(`/admin/branding/logo/${variant}`);
    toast.success("Logo verwijderd");
    onChange?.(null);
  };

  const previewSrc = currentUrl ? (currentUrl.startsWith("http") ? currentUrl : `${process.env.REACT_APP_BACKEND_URL}${currentUrl}`) : null;

  return (
    <div className="space-y-3">
      <Label>{label}</Label>
      <div className={`rounded-oc border border-oc-border p-5 flex items-center gap-4 ${dark ? "bg-[#003B82]" : "bg-oc-subtle"}`}>
        <div className={`w-32 h-16 rounded flex items-center justify-center ${dark ? "border border-white/20" : "bg-white border border-oc-border"}`}>
          {previewSrc ? (
            <img src={previewSrc} alt="Logo" className="max-h-12 max-w-[110px]" />
          ) : (
            <span className={`text-[12px] ${dark ? "text-white/50" : "text-oc-textSecondary"}`}>
              <ImageIcon className="w-5 h-5 mx-auto" />
            </span>
          )}
        </div>
        <div className="flex-1 space-y-1">
          <p className={`text-[14px] font-semibold ${dark ? "text-white" : "text-oc-primary"}`}>
            {currentUrl ? "Logo actief" : "Geen logo — standaard tekst wordt gebruikt"}
          </p>
          <p className={`text-[12px] ${dark ? "text-white/65" : "text-oc-textSecondary"}`}>
            PNG, JPG of SVG · max 2 MB · {dark ? "lichtkleurig variant voor donkere footer" : "donkerkleurig variant voor witte navbar"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className={`rounded-oc-btn ${dark ? "border-white/30 text-white hover:bg-white/10" : "border-oc-border text-oc-primary hover:bg-white"}`}
          >
            <UploadCloud className="w-4 h-4 mr-2" /> {busy ? "Bezig…" : "Upload"}
          </Button>
          {currentUrl && (
            <Button
              type="button"
              variant="ghost"
              onClick={remove}
              className={`rounded-oc-btn ${dark ? "text-white/85 hover:bg-white/10" : "text-oc-excessive hover:bg-oc-excessive/10"}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </div>
    </div>
  );
}

export default function AdminConfig() {
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [digestPreview, setDigestPreview] = useState(null);

  const load = () => api.get("/admin/config").then((r) => setCfg(r.data));
  useEffect(() => {
    load();
    api.get("/admin/config/defaults").then((r) => setDefaults(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    const payload = { ...cfg };
    delete payload.env_status;
    delete payload.updated_at_iso;
    delete payload.logo_header_url;
    delete payload.logo_footer_url;
    await api.put("/admin/config", payload);
    toast.success("Configuratie opgeslagen");
    load();
  };

  const runRetention = async () => {
    const { data } = await api.post("/admin/retention/run");
    toast.success(`Retentie uitgevoerd: ${data.expired} verlopen, ${data.reminders_sent} herinneringen`);
  };

  const [referralStatus, setReferralStatus] = useState(null);

  const loadReferralStatus = async () => {
    try {
      const { data } = await api.get("/admin/referral/candidates");
      setReferralStatus(data);
    } catch {
      toast.error("Kon referral-status niet laden.");
    }
  };

  const runReferralNow = async () => {
    try {
      const { data } = await api.post("/admin/referral/run-now");
      toast.success(`Referral-job uitgevoerd — ${data.sent || 0} uitnodiging(en) verzonden.`);
      await loadReferralStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Job mislukt.");
    }
  };

  const loadDigestPreview = async () => {
    try {
      const { data } = await api.get("/admin/bookkeeping/preview");
      setDigestPreview(data);
    } catch {
      toast.error("Kon voorbeeld niet laden.");
    }
  };

  const sendDigestNow = async () => {
    try {
      const { data } = await api.post("/admin/bookkeeping/run-digest-now");
      toast.success(`Digest verstuurd naar ${data.sent || 0} ontvanger(s) · ${data.rows ?? 0} bestellingen.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verzending mislukt.");
    }
  };

  if (!cfg) return <p>Laden...</p>;
  const env = cfg.env_status || {};

  const bookkeepingEmails = cfg.bookkeeping_emails || [];
  const blogCategories = cfg.blog_categories || defaults?.default_blog_categories || [];

  return (
    <div className="space-y-7">
      <div>
        <h1 className="font-display font-bold text-oc-primary text-[34px]">Technische configuratie</h1>
        <p className="text-oc-textSecondary mt-1">API-sleutels, betalingsmodus, branding, prijzen en boekhouding.</p>
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-3">
        <h2 className="font-display font-semibold text-oc-primary text-[20px]">Status sleutels (alleen-lezen, beheer via .env)</h2>
        <div className="flex flex-wrap gap-2">
          <StatusBadge ok={env.anthropic_key_set} label="Claude" />
          <StatusBadge ok={env.msp_test_key_set} label="MSP Test" />
          <StatusBadge ok={env.msp_live_key_set} label="MSP Live" />
          <StatusBadge ok={env.smtp_configured} label="SMTP" />
        </div>
        <p className="text-[13px] text-oc-textSecondary mt-2">
          {env.mock_mode ? "Mock-modus actief — voeg keys toe in /app/backend/.env" : "Productie-modus actief"}
        </p>
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-5">
        <h2 className="font-display font-semibold text-oc-primary text-[20px]">Prijzen</h2>
        <p className="text-[13px] text-oc-textSecondary">
          Bedragen <strong>incl. 21% BTW</strong>. Gebruik komma of punt als decimaal scheidingsteken.
        </p>

        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 items-end pt-2">
          <div className="space-y-2">
            <Label>Prijs 1 analyse (€)</Label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-oc-textSecondary font-mono">€</span>
              <Input
                data-testid="cfg-analysis-price"
                type="text"
                inputMode="decimal"
                value={
                  cfg.analysis_price_cents == null
                    ? ""
                    : ((cfg.analysis_price_cents || 0) / 100).toFixed(2).replace(".", ",")
                }
                onChange={(e) => {
                  const v = e.target.value.replace(",", ".").replace(/[^0-9.]/g, "");
                  const cents = Math.round((parseFloat(v) || 0) * 100);
                  setCfg({ ...cfg, analysis_price_cents: cents });
                }}
                className="h-12 rounded-oc-btn pl-9 font-mono"
                placeholder="4,99"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Aantal credits (1 analyse = 1 credit)</Label>
            <Input
              type="number"
              value={1}
              readOnly
              className="h-12 rounded-oc-btn font-mono bg-oc-subtle text-oc-textSecondary cursor-not-allowed"
            />
          </div>

          <div className="space-y-2">
            <Label>Prijs 3-pack bundle (€)</Label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-oc-textSecondary font-mono">€</span>
              <Input
                data-testid="cfg-bundle-price"
                type="text"
                inputMode="decimal"
                value={
                  cfg.bundle_price_cents == null
                    ? ""
                    : ((cfg.bundle_price_cents || 0) / 100).toFixed(2).replace(".", ",")
                }
                onChange={(e) => {
                  const v = e.target.value.replace(",", ".").replace(/[^0-9.]/g, "");
                  const cents = Math.round((parseFloat(v) || 0) * 100);
                  setCfg({ ...cfg, bundle_price_cents: cents });
                }}
                className="h-12 rounded-oc-btn pl-9 font-mono"
                placeholder="9,99"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Aantal credits in bundle</Label>
            <Input
              data-testid="cfg-bundle-size"
              type="number"
              min="2"
              max="20"
              value={cfg.bundle_size ?? 3}
              onChange={(e) => setCfg({ ...cfg, bundle_size: Number(e.target.value) || 3 })}
              className="h-12 rounded-oc-btn font-mono"
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-5">
        <h2 className="font-display font-semibold text-oc-primary text-[20px]">Branding (logo)</h2>
        <LogoUpload
          variant="header"
          currentUrl={cfg.logo_header_url}
          label="Logo navbar (donker logo op witte achtergrond)"
          onChange={() => load()}
        />
        <LogoUpload
          variant="footer"
          currentUrl={cfg.logo_footer_url}
          label="Logo footer (lichte versie op donkere achtergrond)"
          dark
          onChange={() => load()}
        />
        <p className="text-[12px] text-oc-textSecondary">
          Tip: Upload bij voorkeur een SVG met transparante achtergrond. Hoogte wordt geschaald naar 36px.
        </p>
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-5">
        <h2 className="font-display font-semibold text-oc-primary text-[20px]">Boekhouding</h2>

        <div className="space-y-3">
          <Label>Modus</Label>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { v: "off", label: "Uit", desc: "Geen automatische verzending." },
              { v: "per_order", label: "Per bestelling", desc: "Elke factuur met PDF naar boekhouder (oude gedrag)." },
              { v: "daily_digest", label: "Dagelijkse CSV", desc: "1 mail per dag met alle bestellingen in CSV (Excel-ready)." },
            ].map((opt) => {
              const active = (cfg.bookkeeping_mode || (cfg.invoice_forwarding_enabled ? "per_order" : "off")) === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  data-testid={`bookkeeping-mode-${opt.v}`}
                  onClick={() => setCfg({ ...cfg, bookkeeping_mode: opt.v, invoice_forwarding_enabled: opt.v !== "off" })}
                  className={`text-left p-4 rounded-oc-btn border transition-all ${
                    active ? "border-oc-primary bg-oc-primary/5 ring-2 ring-oc-primary/20" : "border-oc-border hover:border-oc-primary/40"
                  }`}
                >
                  <p className="font-display font-semibold text-oc-primary text-[14px]">{opt.label}</p>
                  <p className="mt-1 text-[12px] text-oc-textSecondary leading-snug">{opt.desc}</p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Doorstuur-adressen</Label>
          <div className="space-y-2">
            {bookkeepingEmails.map((email, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  value={email}
                  onChange={(e) => {
                    const next = [...bookkeepingEmails];
                    next[i] = e.target.value;
                    setCfg({ ...cfg, bookkeeping_emails: next });
                  }}
                  placeholder="boekhouding@bedrijf.be"
                  className="h-11 rounded-oc-btn"
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setCfg({ ...cfg, bookkeeping_emails: bookkeepingEmails.filter((_, j) => j !== i) })}
                  className="text-oc-excessive hover:bg-oc-excessive/10"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() => setCfg({ ...cfg, bookkeeping_emails: [...bookkeepingEmails, ""] })}
              className="rounded-oc-btn border-oc-border text-oc-primary hover:bg-oc-subtle"
            >
              <Plus className="w-4 h-4 mr-2" /> E-mail toevoegen
            </Button>
          </div>
          <p className="text-[12px] text-oc-textSecondary">
            Voor Octopus: gebruik je upload-mailadres (bv. <em className="font-mono">bedrijfsnaam@octopus.app</em>). Voor klassieke boekhouders: gewoon hun e-mail.
          </p>
        </div>

        {(cfg.bookkeeping_mode === "daily_digest") && (
          <div className="border-t border-oc-border pt-5 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Verzenduur (Europe/Brussels)</Label>
                <Input
                  type="number"
                  min={0}
                  max={23}
                  value={cfg.bookkeeping_digest_hour ?? 23}
                  onChange={(e) => setCfg({ ...cfg, bookkeeping_digest_hour: Math.max(0, Math.min(23, Number(e.target.value) || 0)) })}
                  className="h-11 rounded-oc-btn"
                  data-testid="digest-hour-input"
                />
                <p className="text-[12px] text-oc-textSecondary mt-1">Standaard 23:00. Wijziging is direct actief.</p>
              </div>
            </div>

            <div>
              <Label>CSV-kolommen (selecteer wat in het overzicht meegestuurd wordt)</Label>
              <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {(defaults?.all_csv_columns || []).map((col) => {
                  const selected = (cfg.bookkeeping_csv_columns || defaults?.default_csv_columns || []).includes(col);
                  return (
                    <button
                      type="button"
                      key={col}
                      data-testid={`csv-col-${col}`}
                      onClick={() => {
                        const current = cfg.bookkeeping_csv_columns || defaults?.default_csv_columns || [];
                        const next = selected ? current.filter((c) => c !== col) : [...current, col];
                        setCfg({ ...cfg, bookkeeping_csv_columns: next });
                      }}
                      className={`text-left px-3 py-2 rounded-oc-btn border text-[13px] font-mono transition-colors ${
                        selected ? "border-oc-primary bg-oc-primary/10 text-oc-primary" : "border-oc-border text-oc-textSecondary hover:border-oc-primary/40"
                      }`}
                    >
                      {selected ? "✓ " : ""}{col}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={loadDigestPreview}
                className="rounded-oc-btn border-oc-border text-oc-primary"
                data-testid="digest-preview-btn"
              >
                Voorbeeld bekijken (geen mail)
              </Button>
              <Button
                type="button"
                onClick={sendDigestNow}
                className="rounded-oc-btn bg-oc-primary text-white hover:bg-oc-secondary"
                data-testid="digest-send-now-btn"
              >
                Verstuur testmail nu
              </Button>
            </div>

            {digestPreview && (
              <div className="bg-oc-subtle border border-oc-border rounded-oc p-4">
                <p className="text-[13px] font-semibold text-oc-primary">
                  Periode: {digestPreview.period} · {digestPreview.row_count} bestellingen
                </p>
                <pre className="mt-3 text-[12px] font-mono text-oc-textPrimary overflow-x-auto whitespace-pre-wrap">{digestPreview.csv_preview || "(leeg)"}</pre>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-5">
        <h2 className="font-display font-semibold text-oc-primary text-[20px]">Refer-a-Friend</h2>
        <div className="flex items-center gap-4">
          <Switch
            data-testid="referral-enabled"
            checked={!!(cfg.referral?.enabled ?? true)}
            onCheckedChange={(v) =>
              setCfg({ ...cfg, referral: { ...(cfg.referral || {}), enabled: v } })
            }
          />
          <div>
            <p className="font-display font-semibold text-oc-primary">Verstuur referral-mail automatisch</p>
            <p className="text-oc-textSecondary text-[13px]">
              24u na het rapport krijgt elke klant een mail met een unieke kortingscode om vrienden uit te nodigen. Template is bewerkbaar via E-mails → "referral_invite".
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 pt-2">
          <div className="space-y-2">
            <Label>Korting (%)</Label>
            <Input
              data-testid="referral-percent-off"
              type="number"
              min="1"
              max="99"
              value={cfg.referral?.percent_off ?? 20}
              onChange={(e) =>
                setCfg({
                  ...cfg,
                  referral: { ...(cfg.referral || {}), percent_off: Number(e.target.value) || 20 },
                })
              }
              className="h-12 rounded-oc-btn font-mono"
            />
            <p className="text-[12px] text-oc-textSecondary">Standaard 20% — toegepast op de eerstvolgende bestelling.</p>
          </div>
          <div className="space-y-2">
            <Label>Geldigheidsduur (dagen)</Label>
            <Input
              data-testid="referral-valid-days"
              type="number"
              min="1"
              max="730"
              value={cfg.referral?.valid_days ?? 90}
              onChange={(e) =>
                setCfg({
                  ...cfg,
                  referral: { ...(cfg.referral || {}), valid_days: Number(e.target.value) || 90 },
                })
              }
              className="h-12 rounded-oc-btn font-mono"
            />
            <p className="text-[12px] text-oc-textSecondary">Standaard 90 dagen. Eenmalig bruikbaar per code.</p>
          </div>
        </div>

        {/* Diagnostiek + handmatige trigger */}
        <div className="pt-4 border-t border-oc-border">
          <h3 className="font-display font-semibold text-oc-primary text-[15px]">Diagnostiek &amp; test</h3>
          <p className="text-[13px] text-oc-textSecondary mt-1">
            De cron-job draait dagelijks om <strong>10:00 UTC</strong> (≈ 12:00 BE). Klanten krijgen pas een uitnodiging wanneer hun rapport ≥ 24u geleden is verstuurd. Gebruik de knoppen hieronder om handmatig te testen of klanten in wachtrij te zien.
          </p>
          <div className="flex flex-wrap gap-3 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={loadReferralStatus}
              data-testid="referral-status-btn"
              className="rounded-oc-btn border-oc-border text-oc-primary"
            >
              Toon status
            </Button>
            <Button
              type="button"
              onClick={runReferralNow}
              data-testid="referral-run-now-btn"
              className="rounded-oc-btn bg-oc-primary hover:bg-oc-secondary text-white"
            >
              Verstuur uitnodigingen nu
            </Button>
          </div>
          {referralStatus && (
            <div className="grid lg:grid-cols-3 gap-3 mt-5">
              <div className="bg-oc-subtle border border-oc-border rounded-oc p-4">
                <p className="text-[11px] uppercase tracking-[0.08em] font-bold text-oc-fair">In aanmerking nu</p>
                <p className="font-mono font-bold text-oc-primary text-[24px] mt-1">{referralStatus.eligible_now.length}</p>
                <p className="text-[11px] text-oc-textSecondary mt-1">Klaar om verzonden te worden.</p>
                {referralStatus.eligible_now.slice(0, 3).map((d) => (
                  <p key={d.id} className="text-[11px] text-oc-textSecondary mt-1 truncate">
                    · {d.email}
                  </p>
                ))}
              </div>
              <div className="bg-oc-subtle border border-oc-border rounded-oc p-4">
                <p className="text-[11px] uppercase tracking-[0.08em] font-bold text-oc-textSecondary">In wachtrij (&lt; 24u)</p>
                <p className="font-mono font-bold text-oc-primary text-[24px] mt-1">{referralStatus.waiting_under_24h.length}</p>
                <p className="text-[11px] text-oc-textSecondary mt-1">Te recente rapporten — wachten op 24u-grens.</p>
                {referralStatus.waiting_under_24h.slice(0, 3).map((d) => (
                  <p key={d.id} className="text-[11px] text-oc-textSecondary mt-1 truncate">
                    · {d.email} <span className="text-oc-textSecondary/70">({(d.report_ready_sent_at_iso || "").slice(11, 16)})</span>
                  </p>
                ))}
              </div>
              <div className="bg-oc-subtle border border-oc-border rounded-oc p-4">
                <p className="text-[11px] uppercase tracking-[0.08em] font-bold text-oc-primary">Recent verzonden</p>
                <p className="font-mono font-bold text-oc-primary text-[24px] mt-1">{referralStatus.recently_sent.length}</p>
                <p className="text-[11px] text-oc-textSecondary mt-1">Laatste uitnodigingen.</p>
                {referralStatus.recently_sent.slice(0, 3).map((d) => (
                  <p key={d.id} className="text-[11px] text-oc-textSecondary mt-1 truncate">
                    · {d.email} <span className="font-mono text-oc-secondary">{d.referral_code}</span>
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-5">
        <h2 className="font-display font-semibold text-oc-primary text-[20px]">Tracking & Pixels</h2>
        <p className="text-[13px] text-oc-textSecondary">
          Plak de ID's hieronder. We injecteren de snippets correct in <code className="text-[11px] bg-oc-subtle px-1 rounded">&lt;head&gt;</code> + <code className="text-[11px] bg-oc-subtle px-1 rounded">&lt;body&gt;</code>. <strong>Vraag eerst toestemming via je cookiebanner</strong> als je in EU werkt.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Facebook Pixel ID</Label>
            <Input
              data-testid="cfg-fb-pixel"
              value={cfg.tracking?.fb_pixel_id ?? ""}
              onChange={(e) => setCfg({ ...cfg, tracking: { ...(cfg.tracking || {}), fb_pixel_id: e.target.value.trim() } })}
              placeholder="e.g. 1234567890123456"
              className="h-12 rounded-oc-btn font-mono"
            />
          </div>
          <div className="space-y-2">
            <Label>Google Ads Conversion ID</Label>
            <Input
              data-testid="cfg-google-ads"
              value={cfg.tracking?.google_ads_id ?? ""}
              onChange={(e) => setCfg({ ...cfg, tracking: { ...(cfg.tracking || {}), google_ads_id: e.target.value.trim() } })}
              placeholder="AW-XXXXXXXXX"
              className="h-12 rounded-oc-btn font-mono"
            />
          </div>
          <div className="space-y-2">
            <Label>TikTok Pixel ID</Label>
            <Input
              data-testid="cfg-tiktok-pixel"
              value={cfg.tracking?.tiktok_pixel_id ?? ""}
              onChange={(e) => setCfg({ ...cfg, tracking: { ...(cfg.tracking || {}), tiktok_pixel_id: e.target.value.trim() } })}
              placeholder="C4XXXXXXXXXXXXXXXXXX"
              className="h-12 rounded-oc-btn font-mono"
            />
          </div>
          <div className="space-y-2">
            <Label>LinkedIn Insight Tag ID</Label>
            <Input
              data-testid="cfg-linkedin-insight"
              value={cfg.tracking?.linkedin_insight_id ?? ""}
              onChange={(e) => setCfg({ ...cfg, tracking: { ...(cfg.tracking || {}), linkedin_insight_id: e.target.value.trim() } })}
              placeholder="1234567"
              className="h-12 rounded-oc-btn font-mono"
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-5">
        <h2 className="font-display font-semibold text-oc-primary text-[20px]">B2B Partners</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Maandelijks abonnement (€)</Label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-oc-textSecondary font-mono">€</span>
              <Input
                data-testid="cfg-b2b-price"
                type="text"
                inputMode="decimal"
                value={
                  cfg.b2b_monthly_price_cents == null
                    ? ""
                    : ((cfg.b2b_monthly_price_cents || 0) / 100).toFixed(2).replace(".", ",")
                }
                onChange={(e) => {
                  const v = e.target.value.replace(",", ".").replace(/[^0-9.]/g, "");
                  const cents = Math.round((parseFloat(v) || 0) * 100);
                  setCfg({ ...cfg, b2b_monthly_price_cents: cents });
                }}
                className="h-12 rounded-oc-btn pl-9 font-mono"
                placeholder="29,00"
              />
            </div>
            <p className="text-[12px] text-oc-textSecondary">Wat bedrijven betalen per maand om in aanbevelingen te verschijnen.</p>
          </div>
          <div className="space-y-2">
            <Label>Google Places API key <span className="text-oc-textSecondary text-[12px] font-normal">(optioneel, voor AI-aanbevelingen)</span></Label>
            <Input
              data-testid="cfg-google-places-key"
              type="password"
              value={cfg.google_places_api_key ?? ""}
              onChange={(e) => setCfg({ ...cfg, google_places_api_key: e.target.value })}
              placeholder="AIza..."
              className="h-12 rounded-oc-btn font-mono"
            />
            <p className="text-[12px] text-oc-textSecondary">Eens ingesteld kunnen we naast B2B-partners ook AI-aanbevelingen sturen op basis van reviews/betrouwbaarheid in de regio.</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-5">
        <h2 className="font-display font-semibold text-oc-primary text-[20px]">Meldingen — mislukte analyses</h2>
        <div className="space-y-2">
          <Label>E-mail voor failure notificaties</Label>
          <Input
            data-testid="failure-notification-email"
            type="email"
            value={cfg.failure_notification_email ?? ""}
            placeholder="test@webtron.be"
            onChange={(e) => setCfg({ ...cfg, failure_notification_email: e.target.value })}
            className="h-12 rounded-oc-btn max-w-md"
          />
          <p className="text-[12px] text-oc-textSecondary">
            Zodra een AI-analyse mislukt krijgt dit adres meteen een mail met de fout + klantgegevens, zodat je de klant een gratis nieuwe scan kan sturen via /admin/analyses.
          </p>
        </div>
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-5">
        <h2 className="font-display font-semibold text-oc-primary text-[20px]">MultiSafepay</h2>
        <div className="flex items-center gap-4">
          <Switch
            checked={cfg.multisafepay_mode === "LIVE"}
            onCheckedChange={(v) => setCfg({ ...cfg, multisafepay_mode: v ? "LIVE" : "TEST" })}
            data-testid="msp-toggle"
          />
          <div>
            <p className="font-display font-semibold text-oc-primary">{cfg.multisafepay_mode === "LIVE" ? "LIVE" : "TEST"}</p>
            <p className="text-oc-textSecondary text-[13px]">Schakel om naar live wanneer je productie klaar is.</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-5">
        <h2 className="font-display font-semibold text-oc-primary text-[20px]">Tracking & scripts</h2>
        <div className="space-y-2">
          <Label>Google Analytics 4 Measurement ID</Label>
          <Input value={cfg.ga_measurement_id || ""} placeholder="G-XXXXXXXXXX" onChange={(e) => setCfg({ ...cfg, ga_measurement_id: e.target.value })} className="h-12 rounded-oc-btn" />
        </div>
        <div className="space-y-2">
          <Label>Google Search Console verificatie (meta)</Label>
          <Input value={cfg.gsc_verification || ""} placeholder='<meta name="google-site-verification" content="..." />' onChange={(e) => setCfg({ ...cfg, gsc_verification: e.target.value })} className="h-12 rounded-oc-btn" />
        </div>
        <div className="space-y-2">
          <Label>Header scripts</Label>
          <Textarea value={cfg.header_scripts || ""} onChange={(e) => setCfg({ ...cfg, header_scripts: e.target.value })} className="font-mono text-[13px] rounded-oc-btn min-h-[120px]" />
        </div>
        <div className="space-y-2">
          <Label>Footer scripts</Label>
          <Textarea value={cfg.footer_scripts || ""} onChange={(e) => setCfg({ ...cfg, footer_scripts: e.target.value })} className="font-mono text-[13px] rounded-oc-btn min-h-[120px]" />
        </div>
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-5">
        <h2 className="font-display font-semibold text-oc-primary text-[20px]">Platform</h2>
        <div className="space-y-2">
          <Label>Bewaarperiode (dagen)</Label>
          <Input type="number" value={cfg.data_retention_days || 30} onChange={(e) => setCfg({ ...cfg, data_retention_days: Number(e.target.value) || 30 })} className="h-12 rounded-oc-btn max-w-xs" />
        </div>
        <div className="flex items-center gap-4">
          <Switch checked={!!cfg.maintenance_mode} onCheckedChange={(v) => setCfg({ ...cfg, maintenance_mode: v })} />
          <div>
            <p className="font-display font-semibold text-oc-primary">Onderhoudsmodus</p>
            <p className="text-oc-textSecondary text-[13px]">Toont onderhoudsbanner aan bezoekers.</p>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Onderhoudsboodschap</Label>
          <Textarea value={cfg.maintenance_message || ""} onChange={(e) => setCfg({ ...cfg, maintenance_message: e.target.value })} className="rounded-oc-btn" />
        </div>
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-5">
        <h2 className="font-display font-semibold text-oc-primary text-[20px]">Blog &mdash; auto-publish</h2>
        <p className="text-[13px] text-oc-textSecondary">
          De AI-generator maakt elke ochtend om 09:30 een nieuwe SEO-blog als <strong>concept</strong>. Hieronder kun je instellen of die concepten automatisch gepubliceerd worden na X dagen.
        </p>
        <div className="flex items-center gap-4">
          <Switch
            data-testid="auto-blog-publish-toggle"
            checked={!!cfg.auto_blog_publish_enabled}
            onCheckedChange={(v) => setCfg({ ...cfg, auto_blog_publish_enabled: v })}
          />
          <div>
            <p className="font-display font-semibold text-oc-primary">Auto-publish concepten activeren</p>
            <p className="text-oc-textSecondary text-[13px]">Wanneer aan: concepten zonder kwaliteitswaarschuwingen worden automatisch gepubliceerd zodra ze ouder zijn dan het ingestelde aantal dagen.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Aantal dagen voor auto-publish</Label>
            <Input
              type="number"
              min={1}
              max={30}
              value={cfg.auto_blog_publish_after_days ?? 3}
              onChange={(e) => setCfg({ ...cfg, auto_blog_publish_after_days: Math.max(1, Number(e.target.value) || 3) })}
              className="h-11 rounded-oc-btn"
              data-testid="auto-blog-days-input"
            />
            <p className="text-[12px] text-oc-textSecondary mt-1">Standaard 3. Concepten met kwaliteitswaarschuwingen worden NOOIT auto-gepubliceerd — die moet je handmatig reviewen.</p>
          </div>
        </div>
        <div className="border-t border-oc-border pt-4">
          <div className="flex items-center gap-4">
            <Switch
              data-testid="related-posts-toggle"
              checked={cfg.related_posts_enabled !== false}
              onCheckedChange={(v) => setCfg({ ...cfg, related_posts_enabled: v })}
            />
            <div>
              <p className="font-display font-semibold text-oc-primary">"Verder lezen"-sectie tonen onder elke blog</p>
              <p className="text-oc-textSecondary text-[13px]">3 gerelateerde artikels (zelfde branche/stad/categorie). Verbetert interne SEO-linking.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-5">
        <h2 className="font-display font-semibold text-oc-primary text-[20px]">Blog &mdash; categorieën &amp; thumbnails</h2>
        <p className="text-[13px] text-oc-textSecondary">
          Elke categorie heeft een eigen omslagafbeelding. AI-gegenereerde blogs krijgen automatisch de juiste afbeelding op basis van hun branche.
        </p>
        <div className="space-y-3">
          {blogCategories.map((cat, i) => (
            <div key={cat.slug} className="flex flex-wrap items-center gap-3 p-3 bg-oc-subtle/40 border border-oc-border rounded-oc-btn">
              {cat.thumbnail_url ? (
                <img src={cat.thumbnail_url} alt={cat.name} className="w-16 h-12 object-cover rounded-md border border-oc-border shrink-0" />
              ) : (
                <div className="w-16 h-12 bg-oc-border/40 rounded-md shrink-0" />
              )}
              <div className="flex-1 min-w-[180px]">
                <p className="font-display font-semibold text-oc-primary text-[14px]">{cat.name}</p>
                <p className="text-[11px] font-mono text-oc-textSecondary">/blog?category={cat.slug}</p>
              </div>
              <Input
                value={cat.thumbnail_url || ""}
                onChange={(e) => {
                  const next = [...blogCategories];
                  next[i] = { ...cat, thumbnail_url: e.target.value };
                  setCfg({ ...cfg, blog_categories: next });
                }}
                placeholder="https://images.unsplash.com/..."
                className="h-10 rounded-oc-btn min-w-[280px] flex-1 font-mono text-[12px]"
                data-testid={`category-thumb-${cat.slug}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={save} data-testid="config-save" className="bg-oc-primary hover:bg-oc-secondary text-white rounded-oc-btn h-12 px-7 font-semibold">
          <Save className="w-4 h-4 mr-2" /> Opslaan
        </Button>
        <Button onClick={runRetention} variant="outline" className="rounded-oc-btn h-12 px-7 border-oc-border text-oc-primary hover:bg-oc-subtle font-semibold">
          <RefreshCw className="w-4 h-4 mr-2" /> Forceer retentie
        </Button>
      </div>
    </div>
  );
}
